import { createContext, CSSProperties, ReactNode, useState } from "react";
import { TabsProps } from "antd";
import { TabWrap } from "./style";

// 泛型 T 用于动态传递 props 类型
type OpenTab<T> = (type: string, props: T) => void;
export type TabProps = { key: string; children: ReactNode; label: string };

export interface CustomTabPaneContextType<T> {
  tabs: TabsProps["items"];
  setTabs: React.Dispatch<React.SetStateAction<TabsProps["items"]>>;
  onOpen: OpenTab<T>;
  onClose: (key: string) => void;
}

const defaultContextValue: CustomTabPaneContextType<any> = {
  tabs: [],
  setTabs: () => { },
  onOpen: () => { },
  onClose: () => { },
};

export const CustomTabPaneContext = createContext(defaultContextValue);

interface CustomTabPaneProps<T> {
  defaultKey: string;
  defaultTabs: TabsProps["items"];
  onOpens: (info: T) => { [key: string]: TabProps };
  style?: CSSProperties;
}

const CustomTabPane = <T,>({ defaultKey, defaultTabs, onOpens, style }: CustomTabPaneProps<T>) => {
  const [activeKey, setActiveKey] = useState<string>(defaultKey);
  const [tabs, setTabs] = useState<TabsProps["items"]>(defaultTabs);

  const onOpen: OpenTab<T> = (type, props) => {
    const otherProps = onOpens(props);
    const existingTab = tabs?.find((item) => item.key === otherProps[type].key);

    if (existingTab) {
      // Tab 已存在，更新其内容
      setTabs(tabs?.map(tab =>
        tab.key === otherProps[type].key ? { ...tab, ...otherProps[type] } : tab
      ));
    } else {
      // Tab 不存在，添加新的
      setTabs([...(tabs ?? []), otherProps[type]]);
    }
    setActiveKey(otherProps[type].key);
  };

  const onClose = (key: string) => {
    let idx = tabs?.findIndex(t => t.key === key);
    let newActKey = idx && tabs ? tabs[idx - 1]?.key : defaultKey
    setActiveKey(newActKey);
    setTabs(tabs?.filter((item) => item.key !== key));
  };

  const onEdit = (
    targetKey: React.MouseEvent | React.KeyboardEvent | string,
    action: 'add' | 'remove'
  ) => {
    if (action === 'remove') {
      onClose(targetKey as string);
    }
  };

  return (
    <CustomTabPaneContext.Provider
      value={{
        tabs,
        setTabs,
        onClose,
        onOpen,
      }}
    >
      <TabWrap
        onEdit={onEdit}
        activeKey={activeKey}
        onChange={setActiveKey}
        type="editable-card"
        items={tabs}
        hideAdd
        style={style}
      />
    </CustomTabPaneContext.Provider>
  );
};

export default CustomTabPane;